import { notFoundRouterRoute } from '@townsquare/404-page-view/__router-route';
import { appsRoute } from '@townsquare/apps-view/__router-route';
import { popupAuthRoute } from '@townsquare/auth/popup-login';
import { capabilityProviderResource } from '@townsquare/capability-provider/resource';
import { chatRouterRoute, createAgentRouterRoute, editAgentRouterRoute } from '@townsquare/chat-view/__router-route';
import { dashboardRouterRoute, dashboardDirectoryRouterRoute } from '@townsquare/dashboards-view/__router-route';
import { dashboardExperienceResource } from '@townsquare/dashboards-view/resources';
import { discoverRoute } from '@townsquare/discover-view/route';
import {
  createKudosEmbedRouterRouteOld,
  goalEmbedRouterRoute,
  goalsDirectoryEmbedRouterRoute,
  labelEmbedRouterRoute,
  createKudosEmbedRouterRoute,
  projectEmbedRouterRoute,
  projectsDirectoryEmbedRouterRoute,
  agentEmbedRouterRoute,
} from '@townsquare/embeds/__router-route';
import { emptyOrNonProvisionedStatusUpdateStateRouterRoute } from '@townsquare/empty-or-not-provisioned-state/__router-route';
import { goalViewRoute } from '@townsquare/goal-view/__router-route';
import {
  goalDirectoryRouterRoute,
  goalNoWorkspaceDirectoryRouterRoute,
} from '@townsquare/goals-directory-view/__router-route';
import { sidebarContentResource } from '@townsquare/griffin-navigation/resources';
import { homebarSideNavigationResource } from '@townsquare/home-layout/resources';
import {
  goalUpdateViewRouterRoute,
  projectUpdateViewRouterRoute,
  recentsViewRouterRoute,
  savedViewsUpdatesRouterRoute,
  tagFeedViewRouterRoute,
  teamUpdatesViewRouterRoute,
} from '@townsquare/home-view/__router-route';
import { homeTabRoute } from '@townsquare/hometab-view/__router-route';
import { notificationsRouterRoute } from '@townsquare/notifications-view/__router-route';
import { productActivationResource } from '@townsquare/product-activation-check/resource';
import { projectRoute } from '@townsquare/project-view/__router-route';
import {
  projectDirectoryRouterRoute,
  projectNoWorkspaceDirectoryRouterRoute,
} from '@townsquare/projects-directory-view/__router-route';
import {
  agentProfileRouterRoute,
  embeddableDirectoryRouterRoute,
  kudosProfileRouterRoute,
  profileRouterRoute,
  teamProfileRouterRoute,
} from '@townsquare/ptc-directory-view/__router-route';
import { readingModeRouterRoute } from '@townsquare/reading-mode-view/__router-route';
import { applyResourcesToRoute, expandGlobalRoute } from '@townsquare/router/create-route';
import { HomepageRouteType, HomepageRoutes } from '@townsquare/router/route-types';
import { rovoEntitlementResource } from '@townsquare/rovo/entitlement';
import { rovoRouterRoute } from '@townsquare/rovo-view/__router-route';
import { searchConfigurationResource } from '@townsquare/search-view';
import { searchRouterRoute } from '@townsquare/search-view/__router-route';
import { noWorkspacePersonalSettingsRouterRoute, settingsRouterRoute } from '@townsquare/settings-view/__router-route';
import { staffDirectoryRouterRoute } from '@townsquare/staff-directory-view/__router-route';
import { tagsDirectoryRouterRoute } from '@townsquare/tags-directory-view/__router-route';
import { topicRouterRoute } from '@townsquare/topic-view/__router-route';
import { followGoalActionRouterRoute } from '@townsquare/url-actions/follow-goal/__router-route';
import { followProjectActionRouterRoute } from '@townsquare/url-actions/follow-project/__router-route';
import { lookupRedirectActionRouterRoute } from '@townsquare/url-actions/lookup-redirect/__router-route';
import { reactionUpdateActionRouterRoute } from '@townsquare/url-actions/reaction-update/__router-route';
import { searchRedirectActionRouterRoute } from '@townsquare/url-actions/search-redirect/__router-route';

// When adding a new route, please also add it to the route-sanity-check E2E test
export const routeDefinitions: HomepageRoutes = [
  // Actions
  followProjectActionRouterRoute,
  followGoalActionRouterRoute,
  lookupRedirectActionRouterRoute,
  reactionUpdateActionRouterRoute,
  searchRedirectActionRouterRoute,

  // Routes
  // staffDirectoryRouterRoute needs to come before profileRouterRoute
  staffDirectoryRouterRoute,
  // profileRouterRoute needs to come before teamProfileRouterRoute and kuodsProfileRouterRoute
  profileRouterRoute,
  kudosProfileRouterRoute,
  teamProfileRouterRoute,
  agentProfileRouterRoute,
  createAgentRouterRoute,
  editAgentRouterRoute,
  chatRouterRoute,
  goalDirectoryRouterRoute,
  goalUpdateViewRouterRoute,
  goalViewRoute,
  notificationsRouterRoute,
  projectDirectoryRouterRoute,
  projectRoute,
  projectUpdateViewRouterRoute,
  readingModeRouterRoute,
  savedViewsUpdatesRouterRoute,
  searchRouterRoute,
  rovoRouterRoute,
  settingsRouterRoute,
  tagFeedViewRouterRoute,
  recentsViewRouterRoute,
  teamUpdatesViewRouterRoute,
  topicRouterRoute,
  tagsDirectoryRouterRoute,
  dashboardRouterRoute,
  dashboardDirectoryRouterRoute,
  appsRoute,
  discoverRoute,

  // Embed routes
  createKudosEmbedRouterRoute,
  createKudosEmbedRouterRouteOld,
  goalEmbedRouterRoute,
  goalsDirectoryEmbedRouterRoute,
  labelEmbedRouterRoute,
  projectEmbedRouterRoute,
  projectsDirectoryEmbedRouterRoute,
  agentEmbedRouterRoute,

  // Fallback
  homeTabRoute,
  popupAuthRoute, // TODO: Justin
  notFoundRouterRoute,
];

export const noWorkspaceRouteDefinitions = [
  goalNoWorkspaceDirectoryRouterRoute,
  projectNoWorkspaceDirectoryRouterRoute,
  notificationsRouterRoute,
  emptyOrNonProvisionedStatusUpdateStateRouterRoute,
  embeddableDirectoryRouterRoute,
  profileRouterRoute,
  teamProfileRouterRoute,
  agentProfileRouterRoute,
  createAgentRouterRoute,
  editAgentRouterRoute,
  chatRouterRoute,
  searchRouterRoute,
  rovoRouterRoute,
  discoverRoute,
  recentsViewRouterRoute,
  noWorkspacePersonalSettingsRouterRoute,
  appsRoute,

  // Fallback
  homeTabRoute,
  notFoundRouterRoute,
];

export const routes = routeDefinitions
  .flatMap<HomepageRouteType>(route => {
    if (route.routeType === 'global') {
      return expandGlobalRoute(route);
    }

    return route;
  })
  .map(route => {
    if (route.layout === 'home' && !route.embed) {
      route = applyResourcesToRoute(route, [homebarSideNavigationResource]);
    }

    if (route.routeType === 'global' || route.routeType === 'site') {
      route = applyResourcesToRoute(route, [
        rovoEntitlementResource,
        searchConfigurationResource,
        capabilityProviderResource,
        dashboardExperienceResource,
        sidebarContentResource,
        productActivationResource,
      ]);
    }

    return route;
  });

export const noWorkspaceRoutes = noWorkspaceRouteDefinitions
  .flatMap<HomepageRouteType>(route => {
    if (route.routeType === 'global') {
      return expandGlobalRoute(route);
    }

    return route;
  })
  .map(route => {
    if (route.routeType === 'global' || route.routeType === 'site') {
      route = applyResourcesToRoute(route, [
        rovoEntitlementResource,
        searchConfigurationResource,
        productActivationResource,
      ]);
    }

    return route;
  });
